import { Ref } from 'vue';
import { IPagedList } from '../core/models';
import { IndexDocument } from '../indexes/models';
import { httpClient } from 'src/core/axios/http-client';
import useFileUtils from '@/core/composable/use-file-utils';
import { ResolveParams, ResolveResult } from 'src/core/models/routing';
import { DownloadIndexSharepointDocumentPayload, IKIEnginesState, IPagedListWithDynamicFacets, KIEngine } from './models';

const useKIEnginesStoreActions = (state: Ref<IKIEnginesState>) => {
  const URL_PREFIX = 'kiEngines';

  const loadkIEngines = async (resolveParams: ResolveParams) => {
    const useContainerFiltersResolveParams = (await import('../../core/composable/containers/use-container-filters-resolve-params')).default;
    const { buildResolveParamsFiltersUsingId } = useContainerFiltersResolveParams();
    const filters = buildResolveParamsFiltersUsingId(resolveParams);
    return httpClient.post<void, IPagedList<KIEngine>>(`${URL_PREFIX}`, filters).then((response) => {
      state.value.kiEngines = response.dataList;
      return new ResolveResult(filters.pagination, response, resolveParams);
    });
  };

  const loadkIEngineById = (resolveParams: ResolveParams) => {
    return httpClient.get<void, KIEngine>(`${URL_PREFIX}/${resolveParams.params.id}`).then((response) => {
      state.value.kiEngineOverview = response;
      state.value.kiEngineProperties = response;
    });
  };

  const createDocumentInstance = (data: Partial<IndexDocument>): IndexDocument => {
    const document = new IndexDocument();
    Object.assign(document, data);
    return document;
  };

  const loadIndexDocuments = async (resolveParams: ResolveParams): Promise<ResolveResult> => {
    const useContainerFiltersResolveParams = (await import('../../core/composable/containers/use-container-filters-resolve-params')).default;
    const { buildResolveParamsFilters } = useContainerFiltersResolveParams();
    const id = resolveParams.params.id;
    const filters = buildResolveParamsFilters(resolveParams);
    return httpClient
      .post<void, IPagedListWithDynamicFacets<IndexDocument>>(`${URL_PREFIX}/${id}/index-documents`, filters)
      .then((response) => {
        state.value.facets = response.dynamicFiltersFacets;
        state.value.documents = response.dataList.map(createDocumentInstance);
        return new ResolveResult(filters.pagination, response, resolveParams);
      });
  };

  const loadIndexSearchedDocumentsCount = async (resolveParams: ResolveParams) => {
    const useContainerFiltersResolveParams = (await import('../../core/composable/containers/use-container-filters-resolve-params')).default;
    const { buildResolveParamsFilters } = useContainerFiltersResolveParams();
    const id = resolveParams.params.id;
    const filters = buildResolveParamsFilters(resolveParams);
    return httpClient.post<void, string>(`${URL_PREFIX}/${id}/total-indexes`, filters).then((response) => {
      return (state.value.totalIndexes = response);
    });
  };

  const downloadIndexSharepointDocument = (payload: DownloadIndexSharepointDocumentPayload) => {
    const { blobToBase64, convertAndDownloadDocument } = useFileUtils();
    payload.documentTitle = payload.documentUrl.split('/').pop() || '';
    return httpClient
      .post(`${URL_PREFIX}/download`, payload, {
        responseType: 'blob',
      })
      .then(async (response) => {
        const blob = response instanceof Blob ? response : response.data;
        const base64 = await blobToBase64(blob); 
        convertAndDownloadDocument(base64, payload.documentTitle);
      });
  };

  const downloadDocumentPreview = async (payload: DownloadIndexSharepointDocumentPayload) => {
    // Lazy import to avoid initialization order issues
    const { useCoreStore } = await import('../core/store');
    const coreStore = useCoreStore();
    coreStore.useSilentApiCall= true;
    return httpClient.post<void, string>(`${URL_PREFIX}/download-viewer-document`, payload).then((response) => {
      state.value.activeDocument = response;
      coreStore.useSilentApiCall= false;
    });
  };


  return {
    loadkIEngines,
    loadkIEngineById,
    loadIndexDocuments,
    downloadDocumentPreview,
    downloadIndexSharepointDocument,
    loadIndexSearchedDocumentsCount,
  };
};

export default useKIEnginesStoreActions;
